"use client";
import { FormEvent, useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import {
  AlertCircle,
  ArrowRight,
  Database,
  Loader2,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  energyFinderResponseSchema,
  type EnergyLabelView,
} from "@/lib/energy-label/view";
import EnergyLabelLadder from "./EnergyLabelLadder";
import EnergyLabelResult from "./EnergyLabelResult";

/** `title` and `description` carry the page-meta CTA copy when it exists. */
export default function EnergyLabelFinder({
  title,
  description,
}: {
  title?: string | null;
  description?: string | null;
}) {
  const t = useTranslations("energy-label"),
    locale = useLocale();
  // Start empty: the shell is cached by Cloudflare with the query ignored, so
  // nothing from the URL may reach the server-rendered HTML.
  const [postcode, setPostcode] = useState("");
  const [number, setNumber] = useState("");
  const [result, setResult] = useState<EnergyLabelView | null | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const requestId = useRef(0);
  useEffect(() => () => controller.current?.abort(), []);
  const change = () => {
    requestId.current++;
    controller.current?.abort();
    setLoading(false);
    setResult(undefined);
    setError(null);
  };
  /**
   * Looks up one address. `arrival` marks a lookup started from the URL (a
   * listing card or a banner form), which scrolls the whole panel into view.
   */
  async function run(rawPostcode: string, rawNumber: string, arrival = false) {
    const pc = rawPostcode.replace(/\s/g, "").toUpperCase(),
      n = rawNumber.trim();
    if (
      !/^[1-9]\d{3}[A-Z]{2}$/.test(pc) ||
      !/^[1-9]\d{0,4}(?:[\s\-/]?[a-zA-Z0-9]{1,8})?$/.test(n)
    ) {
      setError("invalid");
      return;
    }
    controller.current?.abort();
    controller.current = new AbortController();
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    setResult(undefined);
    try {
      const params = new URLSearchParams({ postcode: pc, number: n });
      const response = await fetch(
        `${locale === "en" ? "" : `/${locale}`}/api/site/public/resource/energy-label?${params}`,
        { signal: controller.current.signal },
      );
      const data = energyFinderResponseSchema.parse(await response.json());
      if (id !== requestId.current) return;
      if (!data.success) setError(data.error);
      else if (!response.ok) setError("unavailable");
      else setResult(data.data);
      requestAnimationFrame(() => {
        resultRef.current?.focus({ preventScroll: true });
        const mobile = window.matchMedia("(max-width: 1023px)").matches;
        if (!mobile && !arrival) return;
        const target = mobile
          ? resultRef.current
          : document.getElementById("address-check");
        target?.scrollIntoView({
          block: "start",
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
        });
      });
    } catch (e) {
      if (
        id === requestId.current &&
        !(e instanceof DOMException && e.name === "AbortError")
      )
        setError("unavailable");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void run(postcode, number);
  }
  // An address in the URL (listing card, banner form) is read in the browser
  // only and runs straight away. A development double mount is safe: the
  // unmount effect aborts the first pass and the second supersedes it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pc = (params.get("postcode") ?? "").slice(0, 10),
      n = (params.get("number") ?? "").slice(0, 25);
    if (!pc && !n) return;
    setPostcode(pc);
    setNumber(n);
    if (pc && n) void run(pc, n, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      id="address-check"
      className="scroll-mt-24 rounded-3xl bg-secondary p-3 sm:p-5 lg:p-6"
    >
      <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex min-w-0 flex-col gap-5 rounded-2xl bg-background p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-3">
            <span className="flex size-10 items-center justify-center rounded-xl bg-secondary">
              <Search className="size-5" aria-hidden="true" />
            </span>
            <h2 className="text-xl font-semibold tracking-tight sm:text-2xl">
              {title ?? t("form-title")}
            </h2>
            <p className="text-sm leading-6 text-muted-foreground">
              {description ?? t("form-description")}
            </p>
          </div>
          <form
            onSubmit={submit}
            className="flex flex-col gap-4"
            aria-busy={loading}
            noValidate
          >
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="energy-postcode">{t("postcode")}</Label>
                <Input
                  id="energy-postcode"
                  name="postcode"
                  value={postcode}
                  onChange={(e) => {
                    change();
                    setPostcode(e.target.value);
                  }}
                  maxLength={10}
                  autoComplete="postal-code"
                  placeholder="1011 AB"
                  aria-describedby={`energy-postcode-hint${error === "invalid" ? " energy-error" : ""}`}
                  aria-invalid={error === "invalid"}
                  className="h-12 uppercase md:text-base"
                />
                <p
                  id="energy-postcode-hint"
                  className="text-xs text-muted-foreground"
                >
                  {t("postcode-hint")}
                </p>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="energy-number">{t("number")}</Label>
                <Input
                  id="energy-number"
                  name="number"
                  value={number}
                  onChange={(e) => {
                    change();
                    setNumber(e.target.value);
                  }}
                  maxLength={25}
                  autoComplete="off"
                  placeholder="12 A"
                  aria-describedby={`energy-number-hint${error === "invalid" ? " energy-error" : ""}`}
                  aria-invalid={error === "invalid"}
                  className="h-12 md:text-base"
                />
                <p
                  id="energy-number-hint"
                  className="text-xs text-muted-foreground"
                >
                  {t("number-hint")}
                </p>
              </div>
            </div>
            <Button
              disabled={loading}
              type="submit"
              className="h-12 w-full gap-2 rounded-xl text-sm font-semibold sm:h-12"
            >
              {loading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="size-4" aria-hidden="true" />
              )}
              {t(loading ? "loading" : "submit")}
            </Button>
            {(postcode || number) && (
              <button
                type="button"
                onClick={() => {
                  change();
                  setPostcode("");
                  setNumber("");
                }}
                className="mx-auto flex min-h-10 items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" aria-hidden="true" />
                {t("reset")}
              </button>
            )}
          </form>
          <p className="mt-auto flex items-start gap-2 border-t border-border pt-4 text-xs leading-5 text-muted-foreground">
            <ShieldCheck className="mt-0.5 size-4 flex-none" aria-hidden="true" />
            {t("privacy")}
          </p>
        </div>

        <div
          ref={resultRef}
          tabIndex={-1}
          className="min-w-0 scroll-mt-24 rounded-2xl bg-background p-5 shadow-sm outline-none sm:p-6"
          aria-live="polite"
          aria-atomic="true"
        >
          {loading ? (
            <div className="flex min-h-[24rem] flex-col items-center justify-center gap-3 text-center">
              <Loader2
                className="size-8 animate-spin text-muted-foreground"
                aria-hidden="true"
              />
              <p className="text-sm text-muted-foreground">{t("loading")}</p>
            </div>
          ) : result ? (
            <EnergyLabelResult data={result} />
          ) : (
            <div className="flex flex-col gap-5">
              {error ? (
                <p
                  id="energy-error"
                  role="alert"
                  className="flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-sm leading-6 text-amber-900"
                >
                  <AlertCircle
                    className="mt-1 size-4 flex-none"
                    aria-hidden="true"
                  />
                  {t(error as "invalid")}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-semibold leading-snug">
                    {t(result === null ? "no-address-title" : "empty-title")}
                  </h3>
                  <p className="text-sm leading-6 text-muted-foreground">
                    {t(result === null ? "no-address" : "empty-description")}
                  </p>
                </div>
              )}
              <EnergyLabelLadder />
            </div>
          )}
        </div>
      </div>
      <p className="mt-3 flex items-start justify-end gap-1.5 px-1 text-right text-[11px] leading-4 text-secondary-foreground/75 sm:mt-4">
        <Database className="mt-px size-3.5 flex-none" aria-hidden="true" />
        <span>
          {t.rich("data-source", {
            ep: (chunks) => (
              <a
                href="https://www.ep-online.nl/"
                target="_blank"
                rel="noopener noreferrer"
                title={t("source-ep-title")}
                className="whitespace-nowrap font-semibold text-secondary-foreground underline-offset-2 hover:underline"
              >
                {chunks}
              </a>
            ),
            bag: (chunks) => (
              <a
                href="https://www.pdok.nl/"
                target="_blank"
                rel="noopener noreferrer"
                title={t("source-bag-title")}
                className="whitespace-nowrap font-semibold text-secondary-foreground underline-offset-2 hover:underline"
              >
                {chunks}
              </a>
            ),
          })}
        </span>
      </p>
    </div>
  );
}
